import { useState, useEffect, useRef } from 'react';
import { supabase, recordLogin } from '@/supabaseClient';
import { eventBus } from '@/modules/core/events';
import { navigationState } from '@/modules/core/navigationState';
import { events } from '../events';

// Global state to prevent multiple initializations
let globalAuthState = {
  isInitialized: false,
  authListener: null,
  currentSession: null,
  hasRecordedLogin: false,
};

// Cleanup function for when the app is being destroyed
const cleanupGlobalAuth = () => {
  if (globalAuthState.authListener) {
    globalAuthState.authListener.subscription.unsubscribe();
    globalAuthState.authListener = null;
  }
  globalAuthState.isInitialized = false;
  globalAuthState.currentSession = null;
  globalAuthState.hasRecordedLogin = false;
};

// Listen for page unload to cleanup
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', cleanupGlobalAuth);
}

export function useAuth() {
  const [session, setSession] = useState(globalAuthState.currentSession);
  const [loading, setLoading] = useState(true);
  const hasProcessedInitialSession = useRef(false);
  const isMounted = useRef(true);
  
  const updateSession = (newSession) => {
    globalAuthState.currentSession = newSession;
    setSession(newSession);
    
    // Handle login recording globally
    if (newSession?.user?.email && !globalAuthState.hasRecordedLogin) {
      console.log('Recording login for:', newSession.user.email);
      recordLogin(newSession.user.email, import.meta.env.VITE_PUBLIC_APP_ENV);
      globalAuthState.hasRecordedLogin = true;
    }
  };
  
  useEffect(() => {
    const initializeAuth = async () => {
      if (globalAuthState.isInitialized) {
        // If already initialized, just sync with global state
        setSession(globalAuthState.currentSession);
        setLoading(false);
        return;
      }
      
      globalAuthState.isInitialized = true;
      console.log('Initializing auth...');

      try {
        // Get initial session
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        updateSession(data.session);
        if (data.session) {
          hasProcessedInitialSession.current = true;
        }
        setLoading(false);
      } catch (error) {
        console.error('Error checking session:', error);
        setLoading(false);
      }
      
      // Set up auth state listener
      const { data: listener } = supabase.auth.onAuthStateChange(async (event, newSession) => {
        console.log('Auth event:', event);
        
        if (event === 'INITIAL_SESSION') {
          // Only handle if we haven't processed it yet
          if (!hasProcessedInitialSession.current) {
            hasProcessedInitialSession.current = true;
            updateSession(newSession);
          }
        }
        else if (event === 'SIGNED_IN') {
          updateSession(newSession);
          if (newSession?.user?.email) {
            // Track sign in event
            import('/src/services/analytics/index.js').then(({ trackSignIn }) => {
              const authMethod = newSession.user.app_metadata?.provider || 'unknown';
              trackSignIn(authMethod);
            });
            
            // Handle navigation state restoration
            const savedState = navigationState.restore();
            if (savedState) {
              eventBus.publish(events.USER_SIGNED_IN, { 
                user: newSession.user,
                restoreState: savedState
              });
              navigationState.clear();
            } else {
              eventBus.publish(events.USER_SIGNED_IN, { user: newSession.user });
            }
          }
        }
        else if (event === 'TOKEN_REFRESHED') {
          updateSession(newSession);
        }
        else if (event === 'SIGNED_OUT') {
          import('/src/services/analytics/index.js').then(({ trackSignOut }) => {
            trackSignOut();
          });
          
          updateSession(null);
          eventBus.publish(events.USER_SIGNED_OUT, {});
          globalAuthState.hasRecordedLogin = false; // Reset on sign out
        }
      });
      
      globalAuthState.authListener = listener;
    };
    
    initializeAuth();
    
    return () => {
      isMounted.current = false;
      // Don't cleanup global listener on individual component unmount
      // Only cleanup when the whole app is being destroyed
    };
  }, []);
  
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };
  
  return {
    session,
    user: session?.user || null,
    loading,
    signOut,
  };
}